import { Inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ConfigEntity } from '@repository/config/entities/config.entity';
import { EnvironmentApiService } from '@api/config/environment-api.service';
import { WINDOW } from '@providers/window.provider';
import { ApiConfiguration, Configuration } from '../../types/configuration/Configuration';
import { ApiUrlEntity } from './entities/api-url.entity';

@Injectable({
  providedIn: 'root',
})
export class EnvironmentRepositoryService {
  public apiUrl!: ApiUrlEntity;

  public constructor(
    private envApi: EnvironmentApiService,
    @Inject(WINDOW) private window: Window,
  ) {}

  public getConfig(): Observable<Configuration> {
    return this.envApi.getConfig();
  }

  public buildApiUrlEntity(config: Configuration): ApiUrlEntity {
    const baseUrl = this.getBaseUrl(config.api, this.createUrlFallbacks(false));
    const emsBaseWsUrl = this.getBaseUrl(config.ems, this.createUrlFallbacks(true));
    return {
      baseUrl,
      authUrl: `${baseUrl}/v1/auth/`,
      apiUrl: `${baseUrl}/v1/api/`,
      apiUrlV2: `${baseUrl}/v2/api/`,
      apiUtilUrl: `${baseUrl}/v1/util/`,
      emsWsURL: `${emsBaseWsUrl}/ems/ws/ui/`,
      emsIframeURL: `${this.buildEmsHttpBaseUrl(config)}/ems/ui`,
      cardConfigUrl: config.carDatabaseUrl,
      captchaKey: config.captchaApiKey,
      mixpanelApiKey: config.mixpanelApiKey,
      assetsUrl: `https://${config.assetsUrl}`,
    };
  }

  private buildEmsHttpBaseUrl(config: Configuration): string {
    const httpFallback = this.createUrlFallbacks(false);
    if (!config.ems?.protocol) {
      // No EMS protocol defined: fall back to the window.location-derived HTTP base
      return this.getBaseUrl(config.ems, httpFallback);
    }
    const httpProtocol = config.ems.protocol.replace('wss', 'https').replace('ws', 'http');
    const emsHttpConfig: ApiConfiguration = { ...config.ems, protocol: httpProtocol };
    return this.getBaseUrl(emsHttpConfig, httpFallback);
  }

  public getConfigEntity(): Observable<ConfigEntity> {
    return this.getConfig().pipe(
      map((config) => ({
        apiUrlEntity: this.buildApiUrlEntity(config),
        auth0Config: config.auth0,
      })),
    );
  }

  private createUrlFallbacks(webSocket: boolean): ApiConfiguration {
    const location = this.window?.location;
    if (!location) {
      return { protocol: webSocket ? 'ws' : 'http', host: 'localhost' };
    }
    const hostname = location.hostname;
    const host = this.buildApiHostname(hostname);
    const httpProtocol = location.protocol.slice(0, -1);
    const protocol = webSocket ? httpProtocol.replace('http', 'ws') : httpProtocol;
    return { protocol, host };
  }

  private isLocalHostname(hostname: string): boolean {
    return !hostname || hostname === 'localhost' || hostname.endsWith('.localhost');
  }

  private buildApiHostname(hostname: string): string {
    if (this.isLocalHostname(hostname)) {
      return hostname || 'localhost';
    }
    const parts = hostname.split('.');
    return `api.${parts.slice(-2).join('.')}`;
  }

  private getBaseUrl(apiConfiguration: ApiConfiguration, fallbacks: ApiConfiguration): string {
    const protocol = apiConfiguration?.protocol || fallbacks.protocol;
    const host = apiConfiguration?.host || fallbacks.host;
    const port = apiConfiguration?.port;

    const baseUrl = `${protocol}://${host}`;
    if (port) {
      return `${baseUrl}:${port}`;
    }

    return baseUrl;
  }
}
